import type { ApiClient } from './client';
import type {
  ConnectStatusData,
  EventPricing,
  EventRegistration,
  GateScanResult,
  PaidFeatureFlags,
  RegistrationPaymentStatusData,
  RegistrationReviewData,
  SpectatorTicketType,
} from '../../types/event';

export interface RegistrationReviewParams {
  status?: string;
  q?: string;
  class_id?: number;
}

export interface SaveEventPricingPayload {
  event_id: number;
  paid_entry_enabled?: boolean;
  vehicle_entry_price_cents?: number;
  currency?: string;
  refund_policy_text?: string | null;
  payment_deadline_hours?: number;
}

/**
 * Paid events API methods (registration review, checkout, Connect, spectator).
 */
export const paidEventsMethods = {
  async getRegistrationReview(
    eventId: number,
    params?: RegistrationReviewParams
  ): Promise<RegistrationReviewData> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const search = new URLSearchParams({ event_id: String(eventId) });
    if (params?.status) search.set('status', params.status);
    if (params?.q) search.set('q', params.q);
    if (params?.class_id) search.set('class_id', String(params.class_id));
    const response = await this.makeRequest<RegistrationReviewData>(
      `/event_registrations_review.php?${search.toString()}`,
      { method: 'GET' }
    );
    if (response.success && response.data) return response.data;
    throw new Error(response.error || response.message || 'Failed to load review queue');
  },

  async approveRegistration(
    registrationId: number,
    internalNote?: string
  ): Promise<EventRegistration> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const response = await this.makeRequest<EventRegistration>('/registration_approve.php', {
      method: 'POST',
      body: JSON.stringify({
        registration_id: registrationId,
        ...(internalNote ? { internal_note: internalNote } : {}),
      }),
    });
    if (response.success && response.data) return response.data;
    throw new Error(response.error || response.message || 'Failed to approve registration');
  },

  async rejectRegistration(
    registrationId: number,
    opts?: { internal_note?: string; public_reject_note?: string }
  ): Promise<EventRegistration> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const response = await this.makeRequest<EventRegistration>('/registration_reject.php', {
      method: 'POST',
      body: JSON.stringify({
        registration_id: registrationId,
        ...opts,
      }),
    });
    if (response.success && response.data) return response.data;
    throw new Error(response.error || response.message || 'Failed to reject registration');
  },

  async withdrawRegistration(registrationId: number): Promise<EventRegistration> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const response = await this.makeRequest<EventRegistration>('/registration_withdraw.php', {
      method: 'POST',
      body: JSON.stringify({ registration_id: registrationId }),
    });
    if (response.success && response.data) return response.data;
    throw new Error(response.error || response.message || 'Failed to withdraw registration');
  },

  async bulkApproveRegistrations(
    eventId: number,
    registrationIds: number[]
  ): Promise<{ approved: number; failed: Array<{ id: number; error: string }> }> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const response = await this.makeRequest<{
      approved: number;
      failed: Array<{ id: number; error: string }>;
    }>('/registration_bulk_approve.php', {
      method: 'POST',
      body: JSON.stringify({ event_id: eventId, registration_ids: registrationIds }),
    });
    if (response.success && response.data) return response.data;
    throw new Error(response.error || response.message || 'Failed to bulk approve');
  },

  async startRegistrationCheckout(registrationId: number): Promise<{ checkout_url: string }> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const response = await this.makeRequest<{ checkout_url: string; order?: Record<string, unknown> }>(
      '/registration_checkout.php',
      {
        method: 'POST',
        body: JSON.stringify({ registration_id: registrationId }),
      }
    );
    if (response.success && response.data?.checkout_url) {
      return { checkout_url: response.data.checkout_url };
    }
    throw new Error(response.error || response.message || 'Failed to start checkout');
  },

  async getRegistrationPaymentStatus(params: {
    registration_id?: number;
    session_id?: string;
  }): Promise<RegistrationPaymentStatusData> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const search = new URLSearchParams();
    if (params.registration_id) search.set('registration_id', String(params.registration_id));
    if (params.session_id) search.set('session_id', params.session_id);
    const response = await this.makeRequest<RegistrationPaymentStatusData>(
      `/registration_payment_status.php?${search.toString()}`,
      { method: 'GET' }
    );
    if (response.success && response.data) return response.data;
    throw new Error(response.error || response.message || 'Failed to load payment status');
  },

  async getConnectStatus(): Promise<ConnectStatusData> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const response = await this.makeRequest<ConnectStatusData>('/organizer_connect_status.php', {
      method: 'GET',
    });
    if (response.success && response.data) return response.data;
    throw new Error(response.error || response.message || 'Failed to load payout status');
  },

  async startConnectOnboarding(): Promise<{ url: string }> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const response = await this.makeRequest<{ url: string }>('/organizer_connect_onboard.php', {
      method: 'POST',
      body: JSON.stringify({}),
    });
    if (response.success && response.data?.url) return response.data;
    throw new Error(response.error || response.message || 'Failed to start payout setup');
  },

  async refreshConnectOnboarding(): Promise<{
    onboarding_status: string;
    charges_enabled: boolean;
    payouts_enabled: boolean;
    details_submitted: boolean;
    disabled_reason: string | null;
    ready: boolean;
  }> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const response = await this.makeRequest<{
      onboarding_status: string;
      charges_enabled: boolean;
      payouts_enabled: boolean;
      details_submitted: boolean;
      disabled_reason: string | null;
      ready: boolean;
    }>('/organizer_connect_sync.php', {
      method: 'POST',
      body: JSON.stringify({}),
    });
    if (response.success && response.data) return response.data;
    throw new Error(response.error || response.message || 'Failed to refresh payout status');
  },

  async getEventPricing(eventId: number): Promise<EventPricing | null> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const response = await this.makeRequest<EventPricing>(
      `/event_pricing_get.php?event_id=${encodeURIComponent(String(eventId))}`,
      { method: 'GET' }
    );
    if (response.success && response.data) return response.data;
    throw new Error(response.error || response.message || 'Failed to load event pricing');
  },

  async saveEventPricing(payload: SaveEventPricingPayload): Promise<EventPricing> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const response = await this.makeRequest<EventPricing>('/event_pricing_save.php', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (response.success && response.data) return response.data;
    throw new Error(response.error || response.message || 'Failed to save event pricing');
  },

  async listTicketTypes(eventId: number): Promise<SpectatorTicketType[]> {
    const response = await this.makeRequest<SpectatorTicketType[]>(
      `/event_ticket_types.php?event_id=${encodeURIComponent(String(eventId))}`,
      { method: 'GET' }
    );
    if (response.success && Array.isArray(response.data)) return response.data;
    if (response.success) return [];
    throw new Error(response.error || response.message || 'Failed to load ticket types');
  },

  async createSpectatorCheckout(payload: {
    event_id: number;
    ticket_type_id: number;
    quantity: number;
    holder_name?: string;
  }): Promise<{ checkout_url: string }> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const response = await this.makeRequest<{ checkout_url: string }>('/spectator_checkout.php', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (response.success && response.data?.checkout_url) return response.data;
    throw new Error(response.error || response.message || 'Failed to start ticket checkout');
  },

  async gateScan(payload: {
    event_id: number;
    token: string;
    mode?: 'manual' | 'camera';
  }): Promise<GateScanResult> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const response = await this.makeRequest<GateScanResult>('/event_gate_scan.php', {
      method: 'POST',
      body: JSON.stringify({ mode: 'manual', ...payload }),
    });
    if (response.data) return response.data;
    throw new Error(response.error || response.message || 'Scan failed');
  },

  async getAdminPaymentOps(section: string, params: Record<string, string> = {}): Promise<Record<string, unknown>> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const qs = new URLSearchParams({ section, ...params });
    const response = await this.makeRequest<Record<string, unknown>>(
      `/admin/payment_ops.php?${qs.toString()}`,
      { method: 'GET' }
    );
    if (response.success && response.data) return response.data;
    throw new Error(response.error || response.message || 'Failed to load payment ops');
  },

  async getPaymentReconcile(params: Record<string, string> = {}): Promise<{
    issue_count: number;
    issues: Record<string, unknown>[];
  }> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const qs = new URLSearchParams(params);
    const response = await this.makeRequest<{ issue_count: number; issues: Record<string, unknown>[] }>(
      `/admin/payment_reconcile.php${qs.toString() ? `?${qs.toString()}` : ''}`,
      { method: 'GET' }
    );
    if (response.success && response.data) return response.data;
    throw new Error(response.error || response.message || 'Failed to load reconcile report');
  },

  async repairPaymentIssue(payload: {
    repair: string;
    reason: string;
    registration_id?: number;
    order_id?: number;
    event_id?: number;
  }): Promise<void> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const response = await this.makeRequest('/admin/payment_reconcile.php', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (!response.success) {
      throw new Error(response.error || response.message || 'Repair failed');
    }
  },

  async setPaidFeatureFlag(flag: string, enabled: boolean, reason?: string): Promise<PaidFeatureFlags> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const response = await this.makeRequest<{ flags: PaidFeatureFlags }>('/admin/payment_flags.php', {
      method: 'POST',
      body: JSON.stringify({ flag, enabled, reason }),
    });
    if (response.success && response.data?.flags) return response.data.flags;
    throw new Error(response.error || response.message || 'Failed to update flag');
  },

  async getMyTickets(eventId?: number): Promise<{ tickets: Record<string, unknown>[]; flag_enabled: boolean }> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const qs = eventId ? `?event_id=${encodeURIComponent(String(eventId))}` : '';
    const response = await this.makeRequest<{ tickets: Record<string, unknown>[]; flag_enabled: boolean }>(
      `/my_tickets.php${qs}`,
      { method: 'GET' }
    );
    if (response.success && response.data) return response.data;
    throw new Error(response.error || response.message || 'Failed to load tickets');
  },

  async getGateTotals(eventId: number, q?: string): Promise<{
    totals: Record<string, number | string>;
    matches: Record<string, unknown>[];
    camera_scanning: { available: boolean; reason: string };
  }> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const params = new URLSearchParams({ event_id: String(eventId) });
    if (q) params.set('q', q);
    const response = await this.makeRequest<{
      totals: Record<string, number | string>;
      matches: Record<string, unknown>[];
      camera_scanning: { available: boolean; reason: string };
    }>(`/event_gate_scan.php?${params.toString()}`, { method: 'GET' });
    if (response.success && response.data) return response.data;
    throw new Error(response.error || response.message || 'Failed to load gate totals');
  },

  async downloadEventDayRoster(eventId: number, kind: 'tickets' | 'vehicles' | 'both' = 'both'): Promise<void> {
    if (!this.isAuthenticated()) throw new Error('Authentication required');
    const url = `${this.baseUrl}/event_tickets_export.php?event_id=${encodeURIComponent(String(eventId))}&kind=${encodeURIComponent(kind)}`;
    const res = await fetch(url, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });
    if (!res.ok) {
      throw new Error('Failed to download roster');
    }
    const blob = await res.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `event-${eventId}-roster-${kind}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  },

  async getPaidFeatureFlags(fromAdmin = false): Promise<PaidFeatureFlags> {
    if (fromAdmin) {
      if (!this.isAuthenticated()) throw new Error('Authentication required');
      const response = await this.makeRequest<{ flags: PaidFeatureFlags }>('/admin/payment_flags.php', {
        method: 'GET',
      });
      if (response.success && response.data?.flags) return response.data.flags;
      throw new Error(response.error || response.message || 'Failed to load feature flags');
    }
    try {
      const response = await this.makeRequest<{ flags: PaidFeatureFlags }>('/paid_feature_flags.php', {
        method: 'GET',
      });
      if (response.success && response.data?.flags) return response.data.flags;
    } catch {
      /* public endpoint may not exist; fall back to Connect status for organizers */
    }
    if (this.isAuthenticated()) {
      try {
        const response = await this.makeRequest<ConnectStatusData>('/organizer_connect_status.php', {
          method: 'GET',
        });
        if (response.success && response.data?.flags) return response.data.flags;
      } catch {
        return {};
      }
    }
    return {};
  },
} satisfies ThisType<ApiClient>;
